import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Services
export const getServices = () => api.get('/services');
export const getAllServices = () => api.get('/services/all');
export const createService = (data) => api.post('/services', data);
export const updateService = (id, data) => api.put(`/services/${id}`, data);
export const deleteService = (id) => api.delete(`/services/${id}`);

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Clients
export const getClients = () => api.get('/clients');
export const searchClients = (q) => api.get(`/clients/search?q=${encodeURIComponent(q)}`);
export const getClientByCode = (code) => api.get(`/clients/by-code/${code}`);
export const createClient = (data) => api.post('/clients', data);
export const getClient = (id) => api.get(`/clients/${id}`);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Vehicles
export const getClientVehicles = (clientId) => api.get(`/vehicles/client/${clientId}`);
export const createVehicle = (data) => api.post('/vehicles', data);
export const updateVehicle = (id, data) => api.put(`/vehicles/${id}`, data);

// Orders
export const getOrders = (params) => api.get('/orders', { params });
export const getTodayOrders = () => api.get('/orders/today');
export const createOrder = (data) => api.post('/orders', data);
export const checkoutOrder = (id, data) => api.post(`/orders/${id}/checkout`, data);
export const updateOrderStatus = (id, status) => api.put(`/orders/${id}/status`, { status });
export const getOrder = (id) => api.get(`/orders/${id}`);
export const deleteOrder = (id, reason) => api.delete(`/orders/${id}`, { data: { reason } });

// Appointments
export const getAppointments = (params) => api.get('/appointments', { params });
export const createAppointment = (data) => api.post('/appointments', data);
export const createAppointmentAdmin = (data) => api.post('/appointments/admin', data);
export const updateAppointment = (id, data) => api.put(`/appointments/${id}`, data);
export const cancelAppointment = (id) => api.delete(`/appointments/${id}`);
export const getAvailableBlocks = (date) => api.get(`/appointments/available-blocks?date=${date}`);
export const getOperators = () => api.get('/operators');

// Rewards
export const getRewards = () => api.get('/rewards');
export const createReward = (data) => api.post('/rewards', data);
export const updateReward = (id, data) => api.put(`/rewards/${id}`, data);
export const deleteReward = (id) => api.delete(`/rewards/${id}`);
export const redeemReward = (data) => api.post('/rewards/redeem', data);

// Points
export const getClientPoints = (clientId) => api.get(`/points/${clientId}`);

// Dashboard
export const getDashboardStats = (branchId = null) => api.get(`/dashboard/stats${branchId ? `?branch_id=${branchId}` : ''}`);
export const getDailyReport = (date, branchId = null) => {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (branchId) params.set('branch_id', branchId);
  const qs = params.toString();
  return api.get(`/dashboard/daily-report${qs ? `?${qs}` : ''}`);
};
export const getWeeklySales = (branchId = null) => api.get(`/dashboard/weekly-sales${branchId ? `?branch_id=${branchId}` : ''}`);
export const getMonthlyConcentrado = (month, branchId = null) => {
  const params = new URLSearchParams();
  if (month) params.set('month', month);
  if (branchId) params.set('branch_id', branchId);
  const qs = params.toString();
  return api.get(`/finance/monthly-concentrado${qs ? `?${qs}` : ''}`);
};

// Petty Cash (Caja Chica)
export const getPettyCashBalance = () => api.get('/petty-cash/balance');
export const getPettyCashMovements = (date) => api.get(`/petty-cash/movements${date ? `?date=${date}` : ''}`);
export const createPettyCashMovement = (data) => api.post('/petty-cash/movement', data);
export const deletePettyCashMovement = (id) => api.delete(`/petty-cash/movement/${id}`);

// Client Portal
export const getClientOrders = () => api.get('/client-portal/orders');
export const getClientAppointments = () => api.get('/client-portal/appointments');

// Demo Data Management
export const getDemoDataCount = () => api.get('/admin/demo-data/count');
export const deleteDemoData = () => api.delete('/admin/demo-data');

// Branches (Sucursales)
export const getBranches = () => api.get('/branches');
export const getBranchesPublic = () => api.get('/branches/public');
export const createBranch = (data) => api.post('/branches', data);
export const updateBranch = (id, data) => api.put(`/branches/${id}`, data);
export const deleteBranch = (id) => api.delete(`/branches/${id}`);
export const getBranchExpenses = (branchId) => api.get(`/branches/${branchId}/expenses`);
export const createBranchExpense = (branchId, data) => api.post(`/branches/${branchId}/expenses`, data);
export const deleteBranchExpense = (branchId, expenseId) => api.delete(`/branches/${branchId}/expenses/${expenseId}`);

// Settings (Referral Config)
export const getReferralConfig = () => api.get('/settings/referral');
export const updateReferralConfig = (data) => api.put('/settings/referral', data);

// Finance
export const getFinanceSummary = (month, branchId = null) => {
  const params = [];
  if (month) params.push(`month=${month}`);
  if (branchId) params.push(`branch_id=${branchId}`);
  return api.get(`/finance/summary${params.length ? `?${params.join('&')}` : ''}`);
};
export const getDailySales = (month) => api.get(`/finance/daily-sales${month ? `?month=${month}` : ''}`);

// Cash session report (corte de caja)
export const getCashSessionReport = (id) => api.get(`/cash-sessions/${id}/report`);

// Auth
export const changePin = (currentPin, newPin) => api.post('/auth/change-pin', { current_pin: currentPin, new_pin: newPin });

// Clients - manual points grant (admin only)
export const grantClientPoints = (clientId, points, note) =>
  api.post(`/clients/${clientId}/points/grant`, { points, note });

// Detailing module
export const getDetailingClients = () => api.get('/detailing/clients');
export const createDetailingClient = (data) => api.post('/detailing/clients', data);
export const updateDetailingClient = (id, data) => api.put(`/detailing/clients/${id}`, data);
export const deleteDetailingClient = (id) => api.delete(`/detailing/clients/${id}`);

export const getDetailingServices = () => api.get('/detailing/services');
export const createDetailingService = (data) => api.post('/detailing/services', data);
export const updateDetailingService = (id, data) => api.put(`/detailing/services/${id}`, data);
export const deleteDetailingService = (id) => api.delete(`/detailing/services/${id}`);

export const getDetailingQuotes = (status = null) =>
  api.get(`/detailing/quotes${status ? `?status=${status}` : ''}`);
export const getDetailingQuote = (id) => api.get(`/detailing/quotes/${id}`);
export const createDetailingQuote = (data) => api.post('/detailing/quotes', data);
export const updateDetailingQuoteStatus = (id, status) => api.put(`/detailing/quotes/${id}/status`, { status });
export const deleteDetailingQuote = (id) => api.delete(`/detailing/quotes/${id}`);
export const regenerateDetailingShareToken = (id) => api.post(`/detailing/quotes/${id}/regenerate-token`);

// Public detailing quote (no auth)
export const getPublicDetailingQuote = (token) => api.get(`/detailing/public/quotes/${token}`);
export const approvePublicDetailingQuote = (token, approvedBy) =>
  api.post(`/detailing/public/quotes/${token}/approve`, { approved_by: approvedBy });
export const rejectPublicDetailingQuote = (token, reason) =>
  api.post(`/detailing/public/quotes/${token}/reject`, { reason });

// Client Rewards (POS)
export const getClientRewards = (clientId) => api.get(`/clients/${clientId}/rewards`);

// Delivery fee (cargo a domicilio / recogida)
export const getDeliveryFee = () => api.get('/settings/delivery-fee');
export const updateDeliveryFee = (amount) => api.put('/settings/delivery-fee', { amount });

// Push & in-app notifications
export const getNotifications = () => api.get('/notifications');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');

// Cash sessions (apertura / cierre de caja por sucursal)
export const getCurrentSession = (branchId) =>
  api.get('/cash-sessions/current', { params: branchId ? { branch_id: branchId } : {} });
export const openCashSession = (data) => api.post('/cash-sessions/open', data);
export const closeCashSession = (id, data) => api.post(`/cash-sessions/${id}/close`, data);
export const listCashSessions = (params) => api.get('/cash-sessions', { params });
export const getCashSession = (id) => api.get(`/cash-sessions/${id}`);

export const apiClient = api;
export default api;
